import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
export interface PaymentProvider {
  id: string;
  provider: 'nowpayments' | 'stripe';
  api_key: string | null;
  secret_key: string | null;
  ipn_secret: string | null;
  webhook_secret: string | null;
  is_active: boolean;
  test_mode: boolean;
  created_at: string;
  updated_at: string;
}
async function callAdminConfig(action: string, body?: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-config?action=${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}
export function usePaymentProviders() {
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await callAdminConfig('get-payment-providers');
      setProviders(data.providers || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch payment providers');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);
  const updateProvider = async (
    provider: 'nowpayments' | 'stripe',
    updates: Partial<Omit<PaymentProvider, 'id' | 'provider' | 'created_at' | 'updated_at'>>
  ) => {
    try {
      const data = await callAdminConfig('save-payment-provider', { provider, ...updates });
      if (data.success && data.data) {
        setProviders(prev => {
          const exists = prev.find(p => p.provider === provider);
          if (exists) return prev.map(p => p.provider === provider ? data.data : p);
          return [...prev, data.data];
        });
      }
      return { success: true, data: data.data };
    } catch (err: unknown) {
      const message = err instanceof Error
        ? err.message
        : (typeof err === 'string' ? err : 'Failed to update provider — check console for details');
      console.error('updateProvider failed:', err);
      return { success: false, error: message };
    }
  };
  const toggleProviderStatus = async (provider: 'nowpayments' | 'stripe', isActive: boolean) => {
    return updateProvider(provider, { is_active: isActive });
  };
  const getActiveProvider = useCallback((type: 'nowpayments' | 'stripe') => {
    return providers.find(p => p.provider === type && p.is_active);
  }, [providers]);
  return {
    providers,
    loading,
    error,
    fetchProviders,
    updateProvider,
    toggleProviderStatus,
    getActiveProvider,
  };
}
